import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import About from './components/About';
import HowItWorks from './components/HowItWorks';
import Videos from './components/Videos';
import SellerForm from './components/SellerForm';
import Footer from './components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.greenstateinvestments.com';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'GreenState Investments',
  alternateName: 'GreenState Investments LLC',
  url: SITE_URL,
  logo: 'https://static.readdy.ai/image/a48e56127586ecde9cf388aadcd5ef1b/19c26b1626dd19f7cd936f84555985c9.jpeg',
  foundingDate: '1998',
  description:
    'GreenState Investments is a nationwide real estate investment firm dedicated to helping homeowners sell their properties quickly and easily. We buy houses in any condition, anywhere in the United States.',
  telephone: '+12026009590',
  email: 'info@greenstateinvestments.com',
  areaServed: {
    '@type': 'Country',
    name: 'United States',
  },
  sameAs: [],
};

const realEstateAgentSchema = {
  '@context': 'https://schema.org',
  '@type': 'RealEstateAgent',
  name: 'GreenState Investments',
  url: SITE_URL,
  logo: 'https://static.readdy.ai/image/a48e56127586ecde9cf388aadcd5ef1b/19c26b1626dd19f7cd936f84555985c9.jpeg',
  description:
    'GreenState Investments buys houses fast for cash nationwide. Get a fair cash offer within 24 hours — no repairs, no fees, no hassle. Serving all 50 states since 1998.',
  telephone: '+12026009590',
  email: 'info@greenstateinvestments.com',
  foundingDate: '1998',
  areaServed: {
    '@type': 'Country',
    name: 'United States',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Cash Home Buying Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Fast Cash Home Purchase',
          description:
            'We buy your house for cash in any condition, anywhere in the United States. Get a fair offer within 24 hours with no repairs, no fees, and no commissions.',
        },
      },
    ],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How fast can GreenState Investments buy my house?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We can provide a fair cash offer within 24 hours and close in as little as 7 days, or on your preferred timeline.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need to make repairs before selling my house?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. GreenState Investments buys houses as-is in any condition — from pristine to distressed. You do not need to make any repairs.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are there any fees or commissions when selling to GreenState Investments?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'None at all. There are zero realtor commissions, zero closing costs, and no hidden fees. You keep 100% of the cash offer.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does GreenState Investments buy houses nationwide?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. GreenState Investments purchases homes in all 50 U.S. states. No matter where your property is located, we can make you a cash offer.',
      },
    },
    {
      '@type': 'Question',
      name: 'How does the home selling process work with GreenState Investments?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'It is a simple 3-step process: (1) Submit basic property information through our form in under 2 minutes. (2) Receive a fair, no-obligation cash offer within 24 hours. (3) Accept the offer, choose your closing date, and get cash — as fast as 7 days.',
      },
    },
  ],
};

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToForm = () => {
    const formSection = document.getElementById('seller-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(realEstateAgentSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="w-full px-4 md:px-8 py-2 md:py-3 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/a48e56127586ecde9cf388aadcd5ef1b/19c26b1626dd19f7cd936f84555985c9.jpeg" 
              alt="GreenState Investments" 
              className="h-14 md:h-20 w-auto"
            />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#about" className="text-sm font-semibold text-emerald-700 transition-colors hover:text-emerald-500">
              About Us
            </a>
            <a href="#how-it-works" className="text-sm font-semibold text-emerald-700 transition-colors hover:text-emerald-500">
              How It Works
            </a>
            <a href="#videos" className="text-sm font-semibold text-emerald-700 transition-colors hover:text-emerald-500">
              Success Stories
            </a>
            <button 
              onClick={scrollToForm}
              className="bg-emerald-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Your Cash Offer
            </button>
          </div>
          <button 
            onClick={scrollToForm}
            className="md:hidden bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Offer
          </button>
        </div>
      </nav>

      {/* Sections */}
      <Hero onGetOffer={scrollToForm} />
      <About />
      <HowItWorks />
      <Videos />
      <SellerForm />
      <Footer />
    </div>
  );
}